import Box from '@components/base/Box'
import Modal from '@components/base/Modal'
import { Button } from '@components/inputs/Button'
import { DropdownInput } from '@components/inputs/DropdownInput'
import OptionsInput from '@components/inputs/InputPicker/OptionsInput'
import { TextInput } from '@components/inputs/TextInput'
import { ToggleInput } from '@components/inputs/ToggleInput'
import {
  FormInputType,
  RegistrationField,
  RegistrationFieldInput,
} from '@graphql/__generated/graphql'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'
import { useEffect, useState } from 'react'

export type FieldModalData = RegistrationFieldInput & { editIndex?: number }

export default function RegistrationFieldModal({
  show,
  setShow,
  initialData,
}: {
  show: boolean
  setShow: React.Dispatch<React.SetStateAction<boolean>>
  initialData?: FieldModalData
}) {
  const { formData, setFormData } = useEventEditor()

  const [fieldModalData, setFieldModalData] = useState<
    FieldModalData | undefined
  >(initialData)

  useEffect(() => {
    setFieldModalData(initialData)
  }, [initialData])

  return (
    <Modal
      show={show}
      setShow={setShow}
      title={`${
        fieldModalData?.editIndex !== undefined ? 'Edit' : 'Create'
      } Field`}
    >
      <Box className="flex justify-between items-center">
        <DropdownInput
          className={`my-4 mt-4 mr-2`}
          label="Answer Type"
          placeholder="Select a answer type"
          options={[
            { label: 'Text', value: FormInputType.Text },
            { label: 'Dropdown', value: FormInputType.Dropdown },
            { label: 'Number', value: FormInputType.Number },
            { label: 'Date/Time', value: FormInputType.Date },
            { label: 'Yes/No', value: FormInputType.Bool },
            { label: 'Checkbox', value: FormInputType.Checkbox },
          ]}
          onChange={(data) => {
            setFieldModalData({
              ...fieldModalData,
              type: data,
              unique:
                data === FormInputType.Text ? fieldModalData?.unique : false,
            } as FieldModalData)
          }}
          value={fieldModalData?.type ?? FormInputType.Text}
        />
        <TextInput
          validations={{
            minLength: {
              value: 1,
              message: 'Field name must be at least 1 character',
            },
            maxLength: {
              value: 300,
              message: 'Field name must be at most 300 characters',
            },
          }}
          required
          label="Registration Field"
          placeholder="E.g. Year of Graduation"
          value={fieldModalData?.name}
          onChange={(data) => {
            setFieldModalData({
              ...fieldModalData,
              name: data,
            } as FieldModalData)
          }}
          className={`flex-1`}
        />
      </Box>

      {/* Data to be shared to Attendee List field toggle */}
      <ToggleInput
        errorMessage={undefined}
        selected={fieldModalData?.visibleOnAttendeeList ? 0 : 1}
        centerText
        options={['Yes', 'No']}
        className={`mb-4`}
        value={fieldModalData?.visibleOnAttendeeList ? 'Yes' : 'No'}
        callback={(data) => {
          setFieldModalData({
            ...fieldModalData,
            visibleOnAttendeeList: data === 'Yes' ? true : false,
          } as FieldModalData)
        }}
        label={'Data to be shared to Attendee List'}
      />

      {/* Unique field toggle */}
      <ToggleInput
        errorMessage={undefined}
        show={
          !!fieldModalData?.type && fieldModalData.type === FormInputType.Text
        }
        selected={fieldModalData?.unique ? 0 : 1}
        centerText
        options={['Yes', 'No']}
        className={`mb-4`}
        value={fieldModalData?.unique ? 'Yes' : 'No'}
        callback={(data) => {
          setFieldModalData({
            ...fieldModalData,
            unique: data === 'Yes' ? true : false,
          } as FieldModalData)
        }}
        label={'Unique Answer Required?'}
      />

      {/* Unique field toggle */}
      <ToggleInput
        errorMessage={undefined}
        selected={fieldModalData?.required ? 0 : 1}
        centerText
        options={['Yes', 'No']}
        className={`mb-4`}
        value={fieldModalData?.required ? 'Yes' : 'No'}
        callback={(data) => {
          setFieldModalData({
            ...fieldModalData,
            required: data === 'Yes' ? true : false,
          } as FieldModalData)
        }}
        label={'Answer Required?'}
      />

      {/* Date only toggle */}
      <ToggleInput
        errorMessage={undefined}
        show={
          !!fieldModalData?.type && fieldModalData.type === FormInputType.Date
        }
        selected={fieldModalData?.dateOnly ? 0 : 1}
        centerText
        options={['Date Only', 'Date & Time']}
        className={`mb-4`}
        value={fieldModalData?.dateOnly ? 'Date Only' : 'Date & Time'}
        callback={(data) => {
          setFieldModalData({
            ...fieldModalData,
            dateOnly: data === 'Date Only' ? true : false,
          } as FieldModalData)
        }}
        label={'Date Options'}
      />
      <OptionsInput
        name={''}
        value={fieldModalData?.options}
        show={
          !!fieldModalData?.type &&
          fieldModalData.type === FormInputType.Dropdown
        }
        required={
          !!fieldModalData?.type &&
          fieldModalData.type === FormInputType.Dropdown
        }
        onChange={(data) => {
          setFieldModalData({
            ...fieldModalData,
            options: data,
          } as FieldModalData)
        }}
      />

      <Button
        className="my-4"
        title={`${
          fieldModalData?.editIndex !== undefined ||
          fieldModalData?.editIndex === 0
            ? 'Save'
            : 'Create'
        } Field`}
        type="primary"
        onClick={(e) => {
          e.preventDefault()

          if (!fieldModalData?.name) return

          const fieldData: RegistrationField = {
            name: fieldModalData?.name!,
            unique: fieldModalData?.unique ?? false,
            dateOnly: fieldModalData?.dateOnly ?? false,
            required: fieldModalData?.required ?? false,
            visibleOnAttendeeList:
              fieldModalData?.visibleOnAttendeeList ?? false,
            type: fieldModalData?.type ?? FormInputType.Text,
            ...{
              options:
                fieldModalData?.options?.map((o) => ({
                  label: o?.label ?? o,
                  value: o?.value ?? o,
                })) ?? [],
            },
          }

          const payload = [...(formData?.registrationFields ?? [])]

          if (fieldModalData?.editIndex === 0 || fieldModalData?.editIndex)
            payload[fieldModalData!.editIndex!] = fieldData
          else payload.push(fieldData)

          setFormData({
            ...formData,
            registrationFields: payload,
          })

          setShow(false)
          setFieldModalData({ ...fieldModalData, name: '' })
        }}
      />
    </Modal>
  )
}
